import { useEffect } from 'react'
import firebase from 'firebase/compat/app'
import { useApolloClient } from '@apollo/client'
import {
  setShowUserMenu,
  setUser,
  setUserLoading,
  useAppDispatch,
  useAppSelector
} from '../../redux'
import { UserType } from '../../types'
import { removeCookie, setCookie, useIntercom, useSegment } from '.'

export const useUser = () => {
  const auth = firebase.auth()
  const userCollection = firebase.firestore().collection('users')
  const apollo = useApolloClient()
  const dispatch = useAppDispatch()
  const { updateIntercomUser, logoutIntercom } = useIntercom()
  const { identify } = useSegment()
  const { user, loading } = useAppSelector((state) => state.user)

  const logout = async () => {
    removeCookie('uid')
    removeCookie('jwt')
    dispatch(setUser(undefined))
    dispatch(setShowUserMenu(false))
    await logoutIntercom()
    await apollo.clearStore()
    await auth.signOut()
    window.location.reload()
  }

  const load = async () => {
    try {
      dispatch(setUserLoading(true))

      if (!auth.currentUser) {
        return
      }

      const userData = await getUser()

      if (!userData) {
        return
      }

      dispatch(setUser(userData))
    } catch (err) {
    } finally {
      dispatch(setUserLoading(false))
    }
  }

  const getUser = async (): Promise<UserType | null> => {
    const snap = await userCollection.doc(auth.currentUser?.uid).get()
    const userObj = { id: snap.id, ...snap.data() } as UserType
    const tokenResult = await firebase.auth().currentUser?.getIdTokenResult()

    if (!snap.exists || !auth.currentUser) {
      return null
    }

    setCookie('uid', userObj.id)
    setCookie('jwt', tokenResult?.token || '')

    return userObj
  }

  const handleAuthStateChange = async (newUser: firebase.User | null) => {
    const differentCachedUser = newUser?.email !== user?.email

    if (!user?.id || differentCachedUser) {
      dispatch(setUser(undefined))
      dispatch(setUserLoading(true))
      return
    }

    if (!newUser) {
      dispatch(setUserLoading(false))
      dispatch(setUser(undefined))
      return
    }

    const userData = await getUser()

    if (!userData) {
      return dispatch(setUserLoading(false))
    }

    const token = await auth.currentUser?.getIdTokenResult()

    setCookie('jwt', token?.token || '')
    dispatch(setUser(userData))
    dispatch(setUserLoading(false))
  }

  useEffect(() => auth.onAuthStateChanged(handleAuthStateChange), [])

  useEffect(() => {
    if (!!user?.id) {
      identify(user)
      updateIntercomUser(user)
    }
  }, [user, loading])

  return {
    user,
    loading,
    logout,
    load
  }
}
